import Head from "next/head";
import { serializeJsonLd } from "@/lib/structured-data";
import type { ReactNode } from "react";
import Header from "./Header";
import Footer from "./Footer";
import LegacyBehaviours from "./LegacyBehaviours";
import TranslateTree from "./TranslateTree";
import { useI18n } from "./I18nContext";
import { SITE_URL, seoFor, type PageSeo } from "@/lib/seo";
import { DEFAULT_LOCALE, LOCALES, isLocalizablePath, localizePath } from "@/lib/i18n-config";

type LayoutProps = {
  /** Clean route for this page, e.g. "/about" — keys into lib/seo.ts. */
  route: string;
  children: ReactNode;
  /** Overrides for pages whose SEO is not static (blog posts, treatments). */
  seo?: Partial<PageSeo>;
  /** Extra JSON-LD for this page only. */
  schemas?: object[];
  noindex?: boolean;
  /**
   * Overrides the path-based rule. Blog routes are not localizable by default,
   * but a post authored in the CMS does get a /hi version, so the blog
   * detail page passes this explicitly.
   */
  localizable?: boolean;
};

export default function Layout({
  route,
  children,
  seo,
  schemas,
  noindex,
  localizable: localizableOverride,
}: LayoutProps) {
  const { locale, meta, t } = useI18n();
  const base = seoFor(route);
  const pageSeo = { ...base, ...seo };

  // next.config sets trailingSlash: true, so /about is served at /about/. The
  // canonical has to name the URL that actually resolves, or Search Console
  // reports a redirect mismatch.
  const withSlash = (p: string) => (p.endsWith("/") ? p : `${p}/`);
  const canonicalPath = withSlash(localizePath(pageSeo.canonical, locale, localizableOverride ?? false));
  const canonical = `${SITE_URL}${canonicalPath}`;

  // Blog routes are single-language by default; a CMS-authored post opts in.
  const localizable = localizableOverride ?? isLocalizablePath(pageSeo.canonical);

  // Titles and descriptions live in lib/seo.ts as English; translate them the
  // same way as body copy so Hindi pages get Hindi SERP snippets.
  const title = t(pageSeo.title);
  const description = t(pageSeo.description);

  const image = pageSeo.image || "/assets/img/logotho.png";
  const imageUrl = image.startsWith("https://") ? image : `${SITE_URL}${image}`;
  const breadcrumb = route === "/" ? [] : [{
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("Home"), item: `${SITE_URL}${withSlash(localizePath("/", locale))}` },
      ...(route.startsWith("/blog/") ? [{ "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog/` }] : []),
      { "@type": "ListItem", position: route.startsWith("/blog/") ? 3 : 2, name: title, item: canonical },
    ],
  }];

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />

        {/* next/head ignores Fragment children, so these are emitted as a flat
            array plus a sibling rather than wrapped in <>...</>. */}
        {localizable &&
          LOCALES.map((l) => (
            <link
              key={`alt-${l.code}`}
              rel="alternate"
              hrefLang={l.htmlLang}
              href={`${SITE_URL}${withSlash(localizePath(pageSeo.canonical, l.code, true))}`}
            />
          ))}
        {localizable && (
          <link
            key="alt-x-default"
            rel="alternate"
            hrefLang="x-default"
            href={`${SITE_URL}${withSlash(localizePath(pageSeo.canonical, DEFAULT_LOCALE, true))}`}
          />
        )}

        <meta property="og:title" content={t(pageSeo.ogTitle ?? pageSeo.title)} />
        <meta property="og:description" content={t(pageSeo.ogDescription ?? pageSeo.description)} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:type" content={pageSeo.type ?? "website"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        <meta property="og:locale" content={{ en: "en_IN", hi: "hi_IN", ar: "ar_AR", ru: "ru_RU" }[meta.code]} />
        {noindex && <meta name="robots" content="noindex,nofollow" />}
        {[...breadcrumb, ...(schemas ?? [])].map((schema, i) => (
          <script
            key={i}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
          />
        ))}
      </Head>
      <Header localizable={localizable} />
      <main>
        {/* Page bodies are literal JSX carried over from the PHP pages, so the
            translation happens by walking the tree rather than through props. */}
        <TranslateTree locale={locale}>{children}</TranslateTree>
      </main>
      <Footer />
      <LegacyBehaviours />
    </>
  );
}
